import React from 'react';
import { Link } from 'react-router-dom';

const RecruiterFAQ = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Recruiter Dashboard FAQ
            </h1>
            <Link
              to="/recruiter/dashboard"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="p-6 space-y-8">

            {/* Student Categories */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                Student Categories
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: What are the different student categories on my dashboard?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <p className="mb-3">Students are organized into four categories based on their engagement level:</p>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border border-gray-200 dark:border-gray-700 rounded">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">Category</th>
                            <th className="px-3 py-2 text-left font-medium">Description</th>
                            <th className="px-3 py-2 text-left font-medium">What You Can See</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          <tr>
                            <td className="px-3 py-2 font-medium">Applied</td>
                            <td className="px-3 py-2">Students who have submitted a NACME profile update (resume, transcript, etc.) this cycle</td>
                            <td className="px-3 py-2">Full profile, contact info, resume</td>
                          </tr>
                          <tr>
                            <td className="px-3 py-2 font-medium">Favorited You</td>
                            <td className="px-3 py-2">Students who marked your company as one they're interested in on their intake form</td>
                            <td className="px-3 py-2">Full profile, contact info, resume</td>
                          </tr>
                          <tr>
                            <td className="px-3 py-2 font-medium">Potential</td>
                            <td className="px-3 py-2">Students who meet your criteria (GPA, major, institution, class year) but have <strong>not yet applied</strong> through NACME <strong>and</strong> have <strong>not expressed interest</strong> in your company</td>
                            <td className="px-3 py-2">Anonymous view only ("Candidate #XXXXX") until they engage</td>
                          </tr>
                          <tr>
                            <td className="px-3 py-2 font-medium">Shortlisted/Eligible</td>
                            <td className="px-3 py-2">Your curated list of candidates you've starred from any category</td>
                            <td className="px-3 py-2">Combined view of all shortlisted students</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: Can a student appear in multiple categories?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <p className="mb-2">Yes, students can appear in both <strong>Applied</strong> and <strong>Favorited You</strong> if they did both.</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Applied</strong> - All students who submitted a NACME profile update this cycle (may also appear in Favorited)</li>
                      <li><strong>Favorited You</strong> - All students who expressed interest (may also appear in Applied)</li>
                      <li><strong>Potential</strong> - Only students who have <em>neither</em> applied <em>nor</em> expressed interest</li>
                    </ul>
                    <p className="mt-2"><strong>Total Matches</strong> = unique students matching your criteria. Since Applied and Favorited can overlap, the sum of all three categories may exceed Total Matches.</p>
                    <p className="mt-2">Students from any category can be added to your "Shortlisted" tab once you star them.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Shortlisting & Workflow */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                Shortlisting & Workflow
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: How do I shortlist a student?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Click the star icon (☆) next to any student to add them to your shortlist. The star turns yellow (★) when shortlisted. Shortlisted students appear in the "Shortlisted/Eligible" tab for easy access.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: What actions can I take with shortlisted students?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <p className="mb-2">Once shortlisted, you can:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Request Profile</strong> (Matched students) - Invites anonymous candidates to reveal their profile</li>
                      <li><strong>Send External Link</strong> (Applied/Interested students) - Sends your company application link</li>
                      <li><strong>Send Interview Invite</strong> - Schedules an interview</li>
                      <li><strong>Send Offer</strong> - Extends a formal internship offer</li>
                      <li><strong>View Resume</strong> - Access their uploaded resume</li>
                      <li><strong>Add Notes</strong> - Track your interactions and impressions</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: What's the difference between "Request Profile" and "Send External Link"?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <ul className="list-disc list-inside space-y-2 ml-2">
                      <li><strong>Request Profile:</strong> Invite students to express interest and update their NACME profile, so you can see their full information.</li>
                      <li><strong>Send External Link:</strong> Used for students who already applied or expressed interest. It directs them to your company's application portal.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Hold System */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                Hold System
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: What is the Hold feature?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    The Hold feature allows you to temporarily reserve candidates you're actively pursuing (7-day hold, extendable once for 7 more days). While on hold, other recruiters cannot send offers to that student.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: Who can I place on hold?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    You can only hold students from the "Applied" or "Interested" categories—not anonymous "Matched" students. Students must have engaged with your company first.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: What are the hold limits?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Maximum <strong>5 concurrent holds</strong> per recruiter</li>
                      <li>Initial hold lasts <strong>7 days</strong></li>
                      <li>Can extend <strong>once</strong> for an additional 7 days (requires sending an interview invite first)</li>
                      <li><strong>7-day cooldown</strong> after releasing a student before you can hold them again</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: What does the hold status badge show?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><span className="inline-block px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Blue badge</span>: "You (Xd)" - You hold this student, X days remaining</li>
                      <li><span className="inline-block px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">Purple badge</span>: Shows colleague's name - Another recruiter at your company holds them</li>
                      <li><span className="inline-block px-2 py-0.5 text-xs rounded bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Red badge</span>: "On Hold (Xd)" - A recruiter from a different company holds them</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Offers */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                Offers
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: How do I send an offer to a student?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Shortlist the student first</li>
                      <li>Click "Send Offer" button on their card</li>
                      <li>Fill in the offer details (position title required, other fields optional)</li>
                      <li>Click "Send" - the student receives an email with accept/decline links</li>
                    </ol>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: Can I send offers to students on hold by other recruiters?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    No. If another recruiter has placed a hold on a student, the "Send Offer" button will be disabled and show "(Held)".
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: What happens when a student accepts multiple offers?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    The platform automatically alerts all administrators when a student accepts more than one offer. This ensures proper follow-up and prevents conflicts.
                  </p>
                </div>
              </div>
            </section>

            {/* Notes & Tracking */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                Notes & Tracking
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: How do notes work?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    You can add private notes to any student by clicking the notes icon. Notes auto-save after 1 second of inactivity. Only you and recruiters from your company can see your notes.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: What interactions are tracked?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <p className="mb-2">The system tracks:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>When you viewed a profile</li>
                      <li>External links sent</li>
                      <li>Interview invites sent</li>
                      <li>Offers sent and their status (pending/accepted/declined)</li>
                      <li>Hold activity</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Map View */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                Map View
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: How does the Map View work?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    The <Link to="/recruiter/dashboard/map" className="text-blue-600 hover:underline dark:text-blue-400">Map View</Link> displays students geographically based on their registered addresses. This helps identify candidates near your office locations.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: What do the marker colors mean?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1"></span> <strong>Green</strong> - Applied (highest priority)</li>
                      <li><span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1"></span> <strong>Blue</strong> - Interested/Favorited You</li>
                      <li><span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-1"></span> <strong>Purple</strong> - Matched (anonymous)</li>
                    </ul>
                    <p className="mt-2">When students appear in multiple categories, the color shows their highest-priority status.</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: Why might some students not appear on the map?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <p className="mb-2">Students may not appear if:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Their address couldn't be geocoded (invalid or incomplete address)</li>
                      <li>They're outside the current map view</li>
                      <li>Server-side pagination limits are active (the map shows the most recent/relevant students first)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Verification & Data Quality */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                Verification & Data Quality
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: What does "Verified" vs "Self-reported" mean?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><span className="inline-block px-2 py-0.5 text-xs rounded bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">✓ Verified</span> - University partner has confirmed this data</li>
                      <li><span className="inline-block px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">⏳ Self-reported</span> - Student-provided data awaiting verification</li>
                    </ul>
                    <p className="mt-2">When data differs between self-reported and verified sources, you'll see the old value struck through with the updated value displayed.</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: Why might a student show "Pending" for Status or Eligible?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Status: Pending</strong> - University partner hasn't verified their academic information yet</li>
                      <li><strong>Eligible: Pending</strong> - Eligibility determination is still in process</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Performance & Navigation */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                Performance & Navigation
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: Why does loading sometimes take a moment?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    The dashboard uses server-side pagination (50 students per page) with infinite scroll for optimal performance. Initial load fetches your shortlist and first page of each category. Scroll down to load more students automatically.
                  </p>
                </div>
              </div>
            </section>

            {/* Troubleshooting */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                Troubleshooting
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: A student I shortlisted isn't appearing - why?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <p className="mb-2">Check that:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>You're on the correct tab (try "Shortlisted/Eligible")</li>
                      <li>The student still meets your eligibility criteria</li>
                    </ol>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: Actions are disabled and showing "(RO)" - why?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <p className="mb-2">"(RO)" means Read-Only mode. This appears when:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>An administrator is viewing your dashboard</li>
                      <li>You're in a preview or demo mode</li>
                      <li>Your account has restricted permissions</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: I'm getting an error when trying to place a hold - why?
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300">
                    <p className="mb-2">Common reasons:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>You've reached the 5-hold limit</li>
                      <li>The student is in the "Matched" category (must be Applied or Interested)</li>
                      <li>You released this student within the last 7 days (cooldown period)</li>
                      <li>The hold feature is temporarily disabled by administrators</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact & Support */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                Contact & Support
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Q: How do I get help with the dashboard?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Contact the NACME support team through the Help Desk or email the platform administrators for technical assistance.
                  </p>
                </div>
              </div>
            </section>

          </div>
        </div>

        {/* Back to Dashboard Button */}
        <div className="mt-6 text-center">
          <Link
            to="/recruiter/dashboard"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RecruiterFAQ;
